import {
    analyticsIndex,
    analyticsPages,
    connectPage,
    conservatorIndex,
    eventsHomePage,
    explorePage,
    homePage,
    playerComparisonPage,
    teamComparisonPage,
    playerHomePage,
    rawVideoPage,
    scheduleIndex,
    scoutPages,
    serviceIndex,
    servicePages,
    statisticsIndex,
    statisticsPages,
    teamHomePage
} from 'constants/page-names';
import { productToPath, SCOUT } from 'containers/product-initializer/products';

export const PATHS = {
    home: `/${homePage}`,
    shifts: `/${statisticsIndex}/${statisticsPages.shifts}`,
    goaltenders: `/${statisticsIndex}/${statisticsPages.goaltenders}`,
    playersPage: `/${analyticsIndex}/${analyticsPages.playersPage}`,
    playersHome: `/${playerHomePage}`,
    teamHome: `/${teamHomePage}`,
    eventsHome: `/${eventsHomePage}`,
    explore: `/${explorePage}`,
    scoutPlayers: `${productToPath[SCOUT].path}/${scoutPages.players}`,
    playerComparison: `/${playerComparisonPage}`,
    teamComparison: `/${teamComparisonPage}`,
    playlists: `/${conservatorIndex}`,
    raw: `/${rawVideoPage}`,
    schedule: `/${scheduleIndex}`,
    connect: `/${connectPage}`,
    glossary: `/${serviceIndex}/${servicePages.glossary}`,
    support: `/${serviceIndex}`
};

export const endsWithPath = path => new RegExp(`${path.replace(/\//g, '\\/')}$`);

export const SHIFTS_ACCOUNT_DEFAULT_PATTERNS = [
    endsWithPath(PATHS.home),
    endsWithPath(PATHS.shifts),
    endsWithPath(PATHS.playersPage)
];

export const GOALIE_ACCOUNT_DEFAULT_PATTERNS = [endsWithPath(PATHS.home), endsWithPath(PATHS.goaltenders)];

export const FULL_ACCOUNT_DEFAULT_PATTERN = new RegExp(
    [PATHS.playersHome, PATHS.teamHome, PATHS.eventsHome, PATHS.scoutPlayers]
        .map(path => `${path.replace(/\//g, '\\/')}$`)
        .join('|')
);

export const LAUNCHER_GRID_ORDER = [
    'players',
    'teams',
    'events',
    'explore',
    'playerComparison',
    'teamComparison',
    'playlists',
    'raw',
    'schedule',
    'connect',
    'glossary',
    'support'
];

export const LAUNCHER_LABEL_IDS = {
    home: 'home',
    shifts: 'statistics.shifts',
    goaltenders: 'statistics.goalkeepers',
    playersPage: 'dashboard.playersPage',
    players: 'players',
    teams: 'teams',
    events: 'events',
    explore: 'explore.menu',
    playerComparison: 'launcher.playerComparison',
    teamComparison: 'launcher.teamComparison',
    playlists: 'launcher.playlists',
    raw: 'launcher.rawVideo',
    schedule: 'launcher.schedule',
    connect: 'launcher.connect',
    glossary: 'launcher.glossary',
    support: 'launcher.support'
};

/** Product rule: ensure Glossary appears in the 3x3 when Support exists but Glossary is only a submenu/path. */
export const GLOSSARY_GRID_RULE = {
    key: 'glossary',
    supportKey: 'support',
    supportPathPattern: endsWithPath(PATHS.support),
    glossaryPathPattern: /\/glossary$/,
    buildPath: supportTo => `${supportTo}/${servicePages.glossary}`
};

export const LAUNCHER_KEY_MATCHERS = [
    {
        key: 'players',
        test: to => endsWithPath(PATHS.playersHome).test(to) || endsWithPath(PATHS.scoutPlayers).test(to)
    },
    { key: 'teams', test: to => endsWithPath(PATHS.teamHome).test(to) },
    { key: 'events', test: to => endsWithPath(PATHS.eventsHome).test(to) },
    { key: 'explore', test: to => endsWithPath(PATHS.explore).test(to) },
    { key: 'home', test: to => endsWithPath(PATHS.home).test(to) },
    { key: 'shifts', test: to => endsWithPath(PATHS.shifts).test(to) },
    { key: 'goaltenders', test: to => endsWithPath(PATHS.goaltenders).test(to) },
    { key: 'playersPage', test: to => endsWithPath(PATHS.playersPage).test(to) },
    { key: 'playerComparison', test: to => endsWithPath(PATHS.playerComparison).test(to) },
    { key: 'teamComparison', test: to => endsWithPath(PATHS.teamComparison).test(to) },
    { key: 'playlists', test: to => endsWithPath(PATHS.playlists).test(to) },
    { key: 'raw', test: to => endsWithPath(PATHS.raw).test(to) },
    { key: 'schedule', test: to => endsWithPath(PATHS.schedule).test(to) },
    { key: 'connect', test: to => endsWithPath(PATHS.connect).test(to) },
    {
        key: 'glossary',
        test: to => GLOSSARY_GRID_RULE.glossaryPathPattern.test(to) || endsWithPath(PATHS.glossary).test(to)
    },
    { key: 'support', test: to => GLOSSARY_GRID_RULE.supportPathPattern.test(to) }
];

/**
 * Stored pins are absolute paths (e.g. /premium/player-home). Only pins that match a currently
 * available link count - otherwise after switching product none would match and the header would
 * be empty. Falls back to defaults when no stored pin applies.
 */
export const resolveActivePins = (storedPins, defaultPins, availablePaths) => {
    const available = availablePaths ? new Set(availablePaths) : null;
    const applicable = (storedPins || []).filter(pin => !available || available.has(pin));

    return applicable.length ? applicable : defaultPins;
};

export const POPOVER_ID = 'app-launcher-popover';
